"use strict"

class ProductPage {
  constructor(container) {
    if (!container) return;
    if (container.dataset.productPageInitialized === 'true') return;

    this.container = container;
    this.container.dataset.productPageInitialized = 'true';

    this.variantDataElement = this.container.querySelector('[data-product-variants]');
    this.variantData = this.variantDataElement ? JSON.parse(this.variantDataElement.textContent) : [];

    this.form = this.container.querySelector('[data-product-form]');
    this.variantInput = this.container.querySelector('[data-variant-id]');
    this.addToCartButton = this.container.querySelector('[data-add-to-cart]');
    this.addToCartText = this.container.querySelector('[data-add-to-cart-text]');

    this.priceContainers = this.container.querySelectorAll('[data-product-price]');
    this.comparePriceContainers = this.container.querySelectorAll('[data-product-compare-price]');
    this.stockContainer = this.container.querySelector('[data-product-stock]');
    this.gallery = this.container.querySelector('[data-product-gallery]');
    this.metaContainer = this.container.querySelector('[data-product-meta]');
    this.skuWrap = this.container.querySelector('[data-product-sku-wrap]');
    this.skuValue = this.container.querySelector('[data-product-sku]');
    this.barcodeWrap = this.container.querySelector('[data-product-barcode-wrap]');
    this.barcodeValue = this.container.querySelector('[data-product-barcode]');
    this.metaSep = this.container.querySelector('[data-product-meta-sep]');

    this.optionInputs = this.container.querySelectorAll('[data-option-input]');
    this.quantitySelector = this.container.querySelector('[data-quantity-selector]');
    this.quantityInput = this.container.querySelector('[data-quantity-input]');

    this.i18n = this.getI18n();

    this.currentVariant = this.getInitialVariant();

    this.init();
  }

  init() {
    this.initQuantity();
    this.initVariantPicker();
    this.initGallery();
    this.initAjaxAddToCart();
    this.updateUI();
    window.wishlistUi?.syncButtons(this.container);

    if (window.singleCollectionSliderUi?.init) {
      window.singleCollectionSliderUi.init(this.container);
    }
  }

  getI18n() {
    const i18nElement = this.container.querySelector('[data-product-i18n]');

    if (!i18nElement) {
      return {
        add_to_cart: 'Add to cart',
        sold_out: 'Sold out',
        adding_to_cart: 'Adding to cart...',
        in_stock: 'In stock',
        out_of_stock: 'Out of stock',
        hurry_only_left: 'Hurry! Only {{ count }} left',
      };
    }

    try {
      return JSON.parse(i18nElement.textContent);
    } catch (error) {
      return {
        add_to_cart: 'Add to cart',
        sold_out: 'Sold out',
        adding_to_cart: 'Adding to cart...',
        in_stock: 'In stock',
        out_of_stock: 'Out of stock',
        hurry_only_left: 'Hurry! Only {{ count }} left',
      };
    }
  }

  formatI18n(template, replacements = {}) {
    return Object.entries(replacements).reduce((result, [key, value]) => {
      return result.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), String(value));
    }, template || '');
  }

  getInitialVariant() {
    if (this.variantInput && this.variantData.length) {
      const variantId = Number(this.variantInput.value);
      const matchedVariant = this.variantData.find((variant) => variant.id === variantId);

      if (matchedVariant) {
        return matchedVariant;
      }
    }

    return this.variantData.find((variant) => variant.available) || this.variantData[0] || null;
  }

  initQuantity() {
    if (!this.quantitySelector || !this.quantityInput) return;

    this.quantitySelector.addEventListener('click', (event) => {
      const minusButton = event.target.closest('[data-quantity-minus]');
      const plusButton = event.target.closest('[data-quantity-plus]');

      if (!minusButton && !plusButton) return;

      let value = parseInt(this.quantityInput.value, 10) || 1;

      if (minusButton) {
        value = Math.max(1, value - 1);
      }

      if (plusButton) {
        value += 1;
      }

      this.quantityInput.value = value;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.quantityInput.addEventListener('input', () => {
      const value = parseInt(this.quantityInput.value, 10) || 1;
      this.quantityInput.value = Math.max(1, value);
    });
  }

  initVariantPicker() {
    if (!this.optionInputs.length || !this.variantData.length) return;

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const selectedOptions = this.getSelectedOptions();
        const matchedVariant = this.findVariantByOptions(selectedOptions);

        if (!matchedVariant) return;

        this.currentVariant = matchedVariant;
        this.updateUI();
      });
    });
  }

  getSelectedOptions() {
    const selectedOptions = [];

    const optionGroups = this.container.querySelectorAll('[data-option-group]');

    optionGroups.forEach((group) => {
      const checkedInput = group.querySelector('[data-option-input]:checked');
      selectedOptions.push(checkedInput ? checkedInput.value : null);
    });

    return selectedOptions;
  }

  findVariantByOptions(selectedOptions) {
    return this.variantData.find((variant) => {
      return selectedOptions.every((optionValue, index) => {
        return variant[`option${index + 1}`] === optionValue;
      });
    });
  }

  updateUI() {
    if (!this.currentVariant) return;

    this.updateVariantInput();
    this.updatePrice();
    this.updateButtonState();
    this.updateStock();
    this.updateMeta();
    this.updateImage();
    this.updateUrl();
  }

  updateMeta() {
    if (!this.metaContainer) return;

    const sku = (this.currentVariant.sku || '').trim();
    const barcode = (this.currentVariant.barcode || '').trim();
    const hasSku = Boolean(sku);
    const hasBarcode = Boolean(barcode);

    if (this.skuValue) {
      this.skuValue.textContent = sku;
    }

    if (this.barcodeValue) {
      this.barcodeValue.textContent = barcode;
    }

    if (this.skuWrap) {
      this.skuWrap.toggleAttribute('hidden', !hasSku);
    }

    if (this.barcodeWrap) {
      this.barcodeWrap.toggleAttribute('hidden', !hasBarcode);
    }

    if (this.metaSep) {
      this.metaSep.toggleAttribute('hidden', !(hasSku && hasBarcode));
    }

    this.metaContainer.toggleAttribute('hidden', !(hasSku || hasBarcode));
  }

  updateVariantInput() {
    if (!this.variantInput) return;
    this.variantInput.value = this.currentVariant.id;
  }

  updatePrice() {
    if (!this.priceContainers.length) return;

    const price = this.formatMoney(this.currentVariant.price);
    this.priceContainers.forEach((container) => {
      container.textContent = price;
    });

    const hasComparePrice =
      this.currentVariant.compare_at_price &&
      this.currentVariant.compare_at_price > this.currentVariant.price;

    this.priceContainers.forEach((container) => {
      const priceWrapper = container.closest('.product-price__wrapper');
      if (priceWrapper) {
        priceWrapper.classList.toggle('product-price__wrapper--sale', hasComparePrice);
      }
    });

    this.comparePriceContainers.forEach((container) => {
      if (hasComparePrice) {
        container.textContent = this.formatMoney(this.currentVariant.compare_at_price);
        container.removeAttribute('hidden');
      } else {
        container.textContent = '';
        container.setAttribute('hidden', 'hidden');
      }
    });
  }

  updateButtonState() {
    if (!this.addToCartButton || !this.addToCartText) return;

    if (this.currentVariant.available) {
      this.addToCartButton.disabled = false;
      this.addToCartText.textContent = this.i18n.add_to_cart;
    } else {
      this.addToCartButton.disabled = true;
      this.addToCartText.textContent = this.i18n.sold_out;
    }
  }

  updateStock() {
    if (!this.stockContainer) return;

    if (!this.currentVariant.available) {
      this.stockContainer.textContent = this.i18n.out_of_stock;
      this.stockContainer.classList.add('is-unavailable');
      return;
    }

    this.stockContainer.classList.remove('is-unavailable');

    if (typeof this.currentVariant.inventory_quantity === 'number' && this.currentVariant.inventory_quantity > 0 && this.currentVariant.inventory_quantity <= 10) {
      this.stockContainer.textContent = this.formatI18n(this.i18n.hurry_only_left, {
        count: this.currentVariant.inventory_quantity,
      });
      return;
    }

    this.stockContainer.textContent = this.i18n.in_stock;
  }

  setActiveGalleryMedia(mediaId) {
    if (!this.gallery || mediaId == null) return;

    if (window.productGalleryUi?.setActiveMedia) {
      window.productGalleryUi.setActiveMedia(this.gallery, mediaId);
      return;
    }

    const mediaIdString = String(mediaId);

    this.gallery.querySelectorAll('[data-product-media]').forEach((item) => {
      const isActive = String(item.dataset.mediaId) === mediaIdString;

      if (!isActive) {
        item.querySelectorAll('video').forEach((video) => {
          video.pause();
          try {
            video.currentTime = 0;
          } catch (error) {
            // Ignore browsers that block seeking while loading.
          }
        });

        item.querySelectorAll('iframe').forEach((iframe) => {
          const currentSrc = iframe.getAttribute('src');
          if (currentSrc && currentSrc !== 'about:blank') {
            iframe.dataset.gallerySrc = currentSrc;
          }
          iframe.setAttribute('src', 'about:blank');
        });
      }

      item.classList.toggle('is-active', isActive);

      if (isActive) {
        item.removeAttribute('hidden');
        item.querySelectorAll('iframe').forEach((iframe) => {
          const storedSrc = iframe.dataset.gallerySrc;
          const currentSrc = iframe.getAttribute('src');
          if (storedSrc && (!currentSrc || currentSrc === 'about:blank')) {
            iframe.setAttribute('src', storedSrc);
          }
        });
      } else {
        item.setAttribute('hidden', '');
      }
    });

    this.gallery.querySelectorAll('[data-product-thumbnail]').forEach((button) => {
      const isActive = String(button.dataset.mediaId) === mediaIdString;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  updateImage() {
    if (!this.currentVariant) return;

    const featuredMedia = this.currentVariant.featured_media || this.currentVariant.featured_image || null;
    if (!featuredMedia?.id) return;

    this.setActiveGalleryMedia(featuredMedia.id);
  }

  updateUrl() {
    if (!this.currentVariant || !window.history.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url.toString());
  }

  initGallery() {
    const thumbnailButtons = this.container.querySelectorAll('[data-product-thumbnail]');

    if (!thumbnailButtons.length) return;

    thumbnailButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const mediaId = Number(button.dataset.mediaId);
        if (!mediaId) return;

        this.setActiveGalleryMedia(mediaId);

        const matchedVariant = this.variantData.find(
          (v) => v.featured_media && Number(v.featured_media.id) === mediaId
        );

        if (!matchedVariant || matchedVariant.id === this.currentVariant?.id) return;

        this.currentVariant = matchedVariant;
        this.syncPickerToVariant(matchedVariant);
        this.updateVariantInput();
        this.updatePrice();
        this.updateButtonState();
        this.updateStock();
        this.updateUrl();
      });
    });
  }

  syncPickerToVariant(variant) {
    const optionGroups = this.container.querySelectorAll('[data-option-group]');

    optionGroups.forEach((group, index) => {
      const optionValue = variant[`option${index + 1}`];
      if (!optionValue) return;

      const matchingInput = group.querySelector(
        `[data-option-input][value="${CSS.escape(optionValue)}"]`
      );

      if (matchingInput && !matchingInput.checked) {
        matchingInput.checked = true;
        matchingInput.dispatchEvent(new Event('change', { bubbles: false }));
      }
    });
  }

  formatMoney(cents) {
    const currency =
      window.__wishlistConfig?.currency
      || (typeof Shopify !== 'undefined' && Shopify.currency?.active)
      || 'USD';

    const amount = (cents || 0) / 100;

    if (currency === 'JOD') {
      return `${amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} JD`;
    }

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  initAjaxAddToCart() {
    if (!this.form || !this.variantInput || !this.addToCartText) return;

    this.form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const variantId = Number(this.variantInput.value);
      const quantity = Number(this.quantityInput?.value || 1);
      if (!variantId) return;

      const initialText = this.addToCartText.textContent;

      try {
        if (this.addToCartButton) {
          this.addToCartButton.disabled = true;
          this.addToCartButton.setAttribute('aria-disabled', 'true');
        }
        this.addToCartText.textContent = this.i18n.adding_to_cart;

        const properties = {};
        new FormData(this.form).forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value !== '') properties[match[1]] = value;
        });
        await addCartItem(variantId, quantity > 0 ? quantity : 1, properties);
        refreshCartUi({ openDrawer: true });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Product form add to cart error:', error);
      } finally {
        if (this.addToCartButton) {
          this.addToCartButton.disabled = !this.currentVariant?.available;
          this.addToCartButton.removeAttribute('aria-disabled');
        }
        this.addToCartText.textContent = initialText;
      }
    });
  }
}

const initProductPages = (scope = document) => {
  const productSections = scope.querySelectorAll('[data-product-page]');
  productSections.forEach((section) => {
    new ProductPage(section);
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initProductPages();
});

document.addEventListener('shopify:section:load', (event) => {
  if (!event.target) return;
  initProductPages(event.target);
});

document.addEventListener('shopify:section:reorder', () => {
  initProductPages();
});




// UPSELL

function parseUpsellVariantIds(value) {
  if (!value) return [];

  return value
    .split(',')
    .map((item) => Number(item.trim()))
    .filter(Boolean);
}

function glowBundledCartSectionPayload() {
  const ids = [];
  const drawer = document.querySelector('[data-cart-drawer-root]');
  if (drawer?.dataset.sectionId) {
    ids.push(drawer.dataset.sectionId);
  }

  const cartRoot = document.querySelector('[data-main-cart-root]');
  if (cartRoot?.dataset.sectionId) {
    ids.push(cartRoot.dataset.sectionId);
  }

  if (!ids.length) {
    return null;
  }

  const cartPath = window.__glowRoutes?.cartUrl || '/cart';
  const sections_url = cartPath.startsWith('/') ? cartPath : `/${cartPath}`;

  return {
    sections: ids.join(','),
    sections_url,
  };
}

function glowApplyBundledCartSections(result) {
  if (result?.sections && window.cartUi?.applyBundledSections) {
    window.cartUi.applyBundledSections(result.sections);
  }
}

async function getCartData() {
  const response = await fetch('/cart.js', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to load cart');
  }

  return response.json();
}

async function addCartItem(variantId, quantity = 1, properties = {}) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: Number(variantId),
      quantity,
      properties,
      ...(glowBundledCartSectionPayload() || {}),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to add item to cart');
  }

  const data = await response.json();
  glowApplyBundledCartSections(data);
  return data;
}

async function updateCartItemQuantity(lineKey, quantity) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: lineKey,
      quantity,
      ...(glowBundledCartSectionPayload() || {}),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to update cart item quantity');
  }

  const data = await response.json();
  glowApplyBundledCartSections(data);
  return data;
}

async function addUpsellGiftIfNeeded(section) {
  if (!section) return false;

  const productsWrap = section.querySelector('.product-upsell__products');
  if (!productsWrap) return false;

  const requiredVariantIds = parseUpsellVariantIds(productsWrap.dataset.requiredVariants);
  const giftVariantId = Number(section.dataset.giftVariantId);

  if (!requiredVariantIds.length || !giftVariantId) {
    return false;
  }

  const cart = await getCartData();
  const cartVariantIds = cart.items.map((item) => Number(item.variant_id));

  const hasAllRequiredProducts = requiredVariantIds.every((variantId) =>
    cartVariantIds.includes(variantId)
  );

  const hasGiftAlready = cartVariantIds.includes(giftVariantId);

  if (!hasAllRequiredProducts || hasGiftAlready) {
    return false;
  }

  await addCartItem(giftVariantId, 1, {
    upsell_gift: 'true',
    upsell_required_variant_ids: requiredVariantIds.join(','),
  });

  return true;
}

function refreshCartUi(detail = {}) {
  document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
}

async function removeInvalidUpsellGiftsIfNeeded() {
  const cart = await getCartData();
  const cartVariantIds = new Set(cart.items.map((item) => Number(item.variant_id)));
  const giftLineKeysToRemove = [];

  cart.items.forEach((item) => {
    const properties = item.properties || {};
    const isUpsellGift =
      String(properties.upsell_gift).toLowerCase() === 'true'
      || String(properties._upsell_gift).toLowerCase() === 'true';
    if (!isUpsellGift) return;

    const requiredVariantIds = parseUpsellVariantIds(
      properties.upsell_required_variant_ids || properties._upsell_required_variant_ids
    );
    if (!requiredVariantIds.length) return;

    const hasAllRequiredProducts = requiredVariantIds.every((variantId) =>
      cartVariantIds.has(variantId)
    );

    if (!hasAllRequiredProducts) {
      giftLineKeysToRemove.push(item.key);
    }
  });

  if (!giftLineKeysToRemove.length) return false;

  await Promise.all(giftLineKeysToRemove.map((lineKey) => updateCartItemQuantity(lineKey, 0)));
  return true;
}

let isUpsellGiftSyncInProgress = false;

async function syncUpsellGiftState() {
  if (isUpsellGiftSyncInProgress) return;

  isUpsellGiftSyncInProgress = true;
  try {
    const wasGiftRemoved = await removeInvalidUpsellGiftsIfNeeded();
    if (wasGiftRemoved) {
      refreshCartUi();
      if (window.location.pathname === '/cart') {
        window.location.reload();
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Upsell gift sync error:', error);
  } finally {
    isUpsellGiftSyncInProgress = false;
  }
}

function setAjaxButtonLoadingState(button, loading, loadingText = 'Adding to cart...') {
  if (!button) return;

  if (!button.dataset.initialText) {
    button.dataset.initialText = button.textContent.trim();
  }

  if (loading) {
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    button.textContent = loadingText;
  } else {
    button.classList.remove('is-loading');
    button.removeAttribute('aria-disabled');
    button.textContent = button.dataset.initialText;
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('.product-upsell .product-upsell__card-button');
  if (!button) return;

  event.preventDefault();

  const section = button.closest('.product-upsell');
  const card = button.closest('.product-upsell__card');
  const variantId = Number(button.dataset.variantId || card?.dataset.variantId);

  if (!section || !variantId) return;

  try {
    setAjaxButtonLoadingState(button, true);

    await addCartItem(variantId);

    await addUpsellGiftIfNeeded(section);

    refreshCartUi({ openDrawer: true });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Upsell add to cart error:', error);
  } finally {
    setAjaxButtonLoadingState(button, false);
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-ajax-add-to-cart-form]');
  if (!form) return;

  event.preventDefault();

  const variantInput = form.querySelector('input[name="id"]');
  const quantityInput = form.querySelector('input[name="quantity"]');
  const submitButton = form.querySelector('[data-ajax-add-to-cart-button]');
  const variantId = Number(variantInput?.value);
  const quantity = Number(quantityInput?.value || 1);

  if (!variantId) return;

  try {
    setAjaxButtonLoadingState(submitButton, true);
    await addCartItem(variantId, quantity > 0 ? quantity : 1);
    refreshCartUi({ openDrawer: true });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Add to cart form error:', error);
  } finally {
    setAjaxButtonLoadingState(submitButton, false);
  }
});

document.addEventListener('DOMContentLoaded', () => {
  syncUpsellGiftState();
});

document.addEventListener('cart:refresh', () => {
  syncUpsellGiftState();
});


// ACCORDION

function initProductAccordion(scope = document) {
  const accordions = scope.querySelectorAll('[data-product-accordion]');

  accordions.forEach((accordion) => {
    if (accordion.dataset.accordionInitialized === 'true') return;
    accordion.dataset.accordionInitialized = 'true';

    const items = accordion.querySelectorAll('.product-accordion__item');

    let hasOpenedItem = false;

    items.forEach((item) => {
      const trigger = item.querySelector('[data-accordion-trigger]');
      const panel = item.querySelector('[data-accordion-panel]');

      if (!trigger || !panel) return;

      panel.hidden = false;
      panel.style.overflow = 'hidden';

      if (item.classList.contains('is-open') && !hasOpenedItem) {
        hasOpenedItem = true;
        trigger.setAttribute('aria-expanded', 'true');
        panel.style.height = 'auto';
      } else {
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        panel.style.height = '0px';
      }
    });

    accordion.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-accordion-trigger]');
      if (!trigger) return;

      const item = trigger.closest('.product-accordion__item');
      if (!item) return;

      const isOpen = item.classList.contains('is-open');

      items.forEach((accordionItem) => {
        if (accordionItem !== item && accordionItem.classList.contains('is-open')) {
          closeAccordionItem(accordionItem);
        }
      });

      if (isOpen) {
        closeAccordionItem(item);
      } else {
        openAccordionItem(item);
      }
    });
  });
}

function openAccordionItem(item) {
  const trigger = item.querySelector('[data-accordion-trigger]');
  const panel = item.querySelector('[data-accordion-panel]');
  if (!trigger || !panel) return;

  item.classList.add('is-open');
  trigger.setAttribute('aria-expanded', 'true');

  panel.style.height = `${panel.scrollHeight}px`;

  const onTransitionEnd = (event) => {
    if (event.propertyName !== 'height') return;

    if (item.classList.contains('is-open')) {
      panel.style.height = 'auto';
    }

    panel.removeEventListener('transitionend', onTransitionEnd);
  };

  panel.addEventListener('transitionend', onTransitionEnd);
}

function closeAccordionItem(item) {
  const trigger = item.querySelector('[data-accordion-trigger]');
  const panel = item.querySelector('[data-accordion-panel]');
  if (!trigger || !panel) return;

  panel.style.height = `${panel.scrollHeight}px`;
  panel.offsetHeight;

  item.classList.remove('is-open');
  trigger.setAttribute('aria-expanded', 'false');

  requestAnimationFrame(() => {
    panel.style.height = '0px';
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initProductAccordion();
});

document.addEventListener('shopify:section:load', (event) => {
  if (!event.target) return;
  initProductAccordion(event.target);
});
